import { Modal, Flex } from "antd";
import {
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  SyncOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import {
  BILLING_CYCLE,
  CURRENCY,
  HOTEL_ID_SERIES,
  getHotelType,
  isVendor,
} from "../../../constants/constent";

/**
 * Reusable Vendor Detail Card — compact, horizontal banner.
 *
 * A short, wide snapshot of a vendor's most important data. Uses the app's
 * primary theme color (CSS var --primary-color) for the accent so it adapts
 * to the active tenant theme.
 *
 * Defensive about field shapes: table rows arrive with hotel_id prefixed and
 * hotel_type already a label; ViewVendor passes the raw vendor. Both work.
 *
 * Usage:
 *  - Inline:  <VendorDetailCard vendor={vendor} />
 *  - Modal:   <VendorDetailCard vendor={vendor} asModal open={open} onClose={fn} />
 */
function VendorDetailCard({ vendor, asModal = false, open = false, onClose }) {
  if (!vendor) return null;

  const PRIMARY = "var(--primary-color)";

  const hotelIdLabel = String(vendor.hotel_id || "").startsWith(HOTEL_ID_SERIES)
    ? vendor.hotel_id
    : HOTEL_ID_SERIES + vendor.hotel_id;

  // hotel_type may be a raw value (ViewVendor) or an already-resolved label
  // (table rows). getHotelType returns "Unknown Type" for an unmatched value,
  // which is the signal that it was already a label — fall back to the raw value.
  const resolvedType = getHotelType(vendor.hotel_type);
  const hotelType =
    resolvedType && resolvedType !== "Unknown Type"
      ? resolvedType
      : vendor.hotel_type;

  const initial = (vendor.hotel_name || vendor.vendor_name || "?")
    .trim()
    .charAt(0)
    .toUpperCase();

  const statusColor =
    vendor.status === "active"
      ? "#16a34a"
      : vendor.status === "inactive"
        ? "#dc2626"
        : "#d97706";

  const cycleLabel =
    BILLING_CYCLE.find((c) => c.value === Number(vendor.billing_cycle))
      ?.label || "-";

  const planName =
    vendor.active_plan?.plan_name || vendor.plan_name || "No Plan";

  const planAmount =
    vendor.active_plan_amount !== undefined &&
    vendor.active_plan_amount !== null
      ? `${CURRENCY}${Number(vendor.active_plan_amount).toLocaleString("en-IN")}`
      : "-";

  const fmtDate = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "-");

  const pill = (text, color) => (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color,
        background: "rgba(0,0,0,0.04)",
        border: `1px solid ${color}33`,
        padding: "2px 10px",
        borderRadius: 20,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );

  const meta = (icon, label, value) => (
    <Flex vertical style={{ minWidth: 110 }}>
      <Flex align="center" gap={6}>
        <span style={{ color: PRIMARY, fontSize: 13 }}>{icon}</span>
        <span style={{ fontSize: 10.5, color: "#9aa3af", fontWeight: 500 }}>
          {label}
        </span>
      </Flex>
      <span
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: "#1f2937",
          marginTop: 2,
        }}
      >
        {value}
      </span>
    </Flex>
  );

  const card = (
    <div
      style={{
        width: "100%",
        borderRadius: 12,
        overflow: "hidden",
        background: "#ffffff",
        boxShadow: asModal ? "none" : "0 4px 16px rgba(0,0,0,0.06)",
        border: "1px solid #eef0f5",
        borderLeft: `4px solid ${PRIMARY}`,
        fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      <Flex align="center" wrap gap={16} style={{ padding: "12px 18px" }}>
        {/* Avatar */}
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: PRIMARY,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initial}
        </div>

        {/* Identity */}
        <Flex vertical style={{ minWidth: 180 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
            {vendor.hotel_name}
          </span>
          <Flex align="center" gap={8} style={{ marginTop: 3 }}>
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              {vendor.vendor_name}
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, color: PRIMARY }}>
              {hotelIdLabel}
            </span>
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                color: "#fff",
                background: statusColor,
                padding: "1px 9px",
                borderRadius: 20,
                textTransform: "capitalize",
              }}
            >
              {vendor.status || "NA"}
            </span>
          </Flex>
          <Flex align="center" gap={6} wrap style={{ marginTop: 6 }}>
            {hotelType && pill(hotelType, "#f97316")}
            {pill(planName, "#4f46e5")}
            {/* Auto-Gen is an internal billing control — hidden from vendors */}
            {!isVendor() &&
              pill(
                vendor.auto_bill_generation ? "Auto-Gen ON" : "Auto-Gen OFF",
                vendor.auto_bill_generation ? "#0891b2" : "#dc2626",
              )}
          </Flex>
        </Flex>

        {/* Divider */}
        <div
          style={{
            width: 1,
            alignSelf: "stretch",
            background: "#eef0f5",
            margin: "0 2px",
          }}
        />

        {/* Meta inline */}
        <Flex align="center" gap={22} wrap style={{ flex: 1 }}>
          {meta(<CrownOutlined />, "Plan Amount", planAmount)}
          {meta(<SyncOutlined />, "Billing Cycle", cycleLabel)}
          {meta(
            <CalendarOutlined />,
            "Next Billing",
            fmtDate(vendor.next_billing_date),
          )}
        </Flex>

        {/* Contact */}
        <Flex vertical gap={4} style={{ fontSize: 12, color: "#374151" }}>
          <Flex align="center" gap={6}>
            <PhoneOutlined style={{ color: PRIMARY }} />
            {vendor.hotel_mobile_no || "-"}
          </Flex>
          <Flex align="center" gap={6}>
            <MailOutlined style={{ color: PRIMARY }} />
            {vendor.hotel_email_id || "-"}
          </Flex>
        </Flex>
      </Flex>
    </div>
  );

  if (asModal) {
    return (
      <Modal
        title="Vendor Detail"
        open={open}
        onCancel={onClose}
        footer={null}
        destroyOnClose
        width={760}
      >
        {card}
      </Modal>
    );
  }

  return card;
}

export default VendorDetailCard;
